import React, { useEffect, useState } from "react";
import enforceFieldTemplateDoc from "../../../utils/enforceFieldTemplateDoc";
import { Button, Colors } from "@factorearth/component-library";
import EditFieldTemplate from "../EditFieldTemplate/EditFieldTemplate";
import DrawerTutorialButton from "components/DrawerTutorial/DrawerTutorialButton/DrawerTutorialButton";
import styles from "./TableFieldsEditor.module.scss";
import { FieldTemplate, User } from "db/definitions";
import { Location } from "hooks/useDrawer";

interface TableFieldsEditorProps {
	setupDrawer: (location: Location | "") => void;
	template: FieldTemplate;
	value: { items: FieldTemplate[] };
	handleSubmitTableField: (data: FieldTemplate) => void;
	handleRemoveTableField: (id: string) => void;
	handleNewTableFieldClick: () => void;
	handleRearrangeThings: (key: string, start: number, end: number) => void;
	handleShowDeleteModal: (cb: () => void, docType: string) => void;
	color: Colors;
	online: boolean;
	userDoc: User;
}

function TableFieldsEditor(props: TableFieldsEditorProps) {
	const {
		setupDrawer,
		template,
		value,
		handleSubmitTableField,
		handleRemoveTableField,
		handleNewTableFieldClick,
		handleRearrangeThings,
		handleShowDeleteModal,
		color,
		online,
		userDoc,
	} = props;

	const [editOpen, setEditOpen] = useState(false);

	/**
	 * renders the provided table fields into the dom (pretty much) using the EditFieldTemplate component.
	 * @returns HTML
	 */
	function renderFields() {
		let fields = [];
		let map: Record<string, FieldTemplate> = {};
		if (value.items?.length > 0) {
			for (let i = 0; i < value.items.length; i++) {
				const tableFieldTemplate = value.items[i];
				map[tableFieldTemplate.id] = tableFieldTemplate;
			}

			if (template.rowOrder) {
				for (let i = 0; i < template.rowOrder.length; i++) {
					const id = template.rowOrder[i];
					const dataForTemplate = map[id];

					if (!dataForTemplate) {
						console.error("Missing template html for in map: ", id);
						continue;
					}

					const fullTemplate = enforceFieldTemplateDoc(dataForTemplate);

					// just going to remove table fields here since we dont need for table children
					// if (fullTemplate.tableFields) {
					// 	delete fullTemplate.tableFields;
					// }

					// Need this as a callback with the index basically.
					async function handleOnBlur(item: FieldTemplate) {
						handleSubmitTableField(item);
					}

					const html = (
						<EditFieldTemplate
							key={`edit-field-template-${fullTemplate.id}`}
							fieldTemplate={fullTemplate}
							userDoc={userDoc}
							index={i}
							online={online}
							setupDrawer={setupDrawer}
							updateFieldTemplate={handleOnBlur}
							isLast={i === template.rowOrder.length - 1}
							handleRearrangeField={handleRearrangeTableField}
							handleRemoveFieldTemplate={handleRemoveTableField}
							handleShowDeleteModal={handleShowDeleteModal}
							fromTable={true}
						/>
					);

					fields.push(html);
				}
			}
		} else {
			// there are none
			return (
				<>
					{template.inputType === "table" ? (
						<div style={{ borderBottom: `1px dotted ${color.border.secondary}` }} className={styles.row}>
							<div className={styles.optionsHeader}>
								Edit Table Field
								<div className={styles.fieldBtn_input}>No Fields in Table</div>
							</div>
						</div>
					) : null}
				</>
			);
		}
		const final = (
			<div className={styles.cells_wrapper} data-test="table-cell-list-wrapper">
				{fields}
			</div>
		);
		return final;
	}

	function handleRearrangeTableField(start: number, end: number) {
		// call the cb from UpdateTemplateForm component
		handleRearrangeThings("rowOrder", start, end);
	}

	/**
	 * ahh yes.. the toggler
	 */
	function toggleEditOpen() {
		setEditOpen(!editOpen);
	}

	return (
		<div style={{ background: color.background.secondary }} className={styles.tableWrapper} key={`table-${template.id}`}>
			<div className={styles.row}>
				<div style={{ color: color.text.primary }} className={styles.tableFieldHeader}>
					<div>Table Field Editor</div>
					<DrawerTutorialButton
						helpColor={color.utils.success}
						location="template-manager-table-cells"
						setupDrawer={setupDrawer}
						style={{ border: "0px" }}
					/>

					<div>
						<Button variant="outlined" colorPalette={color} onClick={toggleEditOpen} data-test="open-table-field-editor">
							{editOpen ? "Close Table Field Editor" : "Open Table Field Editor"}
						</Button>
					</div>
				</div>
				{editOpen ? (
					<div className={styles.edit_fields_wrap}>
						<div className={styles.edit_cells_header}>Your Cell Fields</div>
						<div className={styles.field_input}>{renderFields()}</div>
						<div className={styles.table_btns}>
							<Button
								colorPalette={color}
								variant="outlined"
								style={{
									color: color.text.secondary,
									background: color.background.primary,
								}}
								className={styles.addTblField_btn}
								onClick={handleNewTableFieldClick}
								data-test="add-new-field-to-table">
								Add New Field to Table
							</Button>
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
}

export default TableFieldsEditor;
